/**
 * Kleiner Daten-Hook ueber apiFetch: laedt GET-Endpunkte, liefert {data, loading, error, reload}.
 * Bewusst ohne Cache/Query-Lib — die Listen sind klein, Einfachheit schlaegt Infrastruktur.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch, toApiError } from "../lib/api";
import { zeigtLadezustand } from "./ladezustand";

/**
 * Der Umschlag JEDER API-Antwort: `data` plus — je nach Route — weitere Felder daneben.
 *
 * R0.11: Bis hierher las der Hook ausschliesslich `body.data` und warf alles andere weg. Genau
 * daran waere der `reason` der Wetter-Route (R0.10, PR #424) verendet: der Server nennt den Grund,
 * die Oberflaeche bekommt ihn nie zu sehen. `Z` ist deshalb ADDITIV — voreingestellt leer, damit
 * die rund 60 Bestandsaufrufer (`useApi<Foo>(pfad)`) unveraendert weiterlaufen und `zusatz`
 * schlicht ignorieren.
 */
type ApiEnvelope<T, Z> = { data: T; total?: number } & Z;

export function useApi<T, Z extends object = Record<string, never>>(
  path: string | null,
): {
  data: T | null;
  /**
   * Die Felder derselben Antwort NEBEN `data` (z.B. `{ reason }` der Wetter-Route). `null`,
   * solange nichts geladen wurde.
   */
  zusatz: Z | null;
  /**
   * Wann diese Antwort ankam (ISO). `null`, solange nichts geladen wurde.
   *
   * R1.6: Der Stand einer Recherche-Angabe kommt normalerweise aus der Datenbank
   * (`looked_up_at`). Fuer Quellen, die NICHT gespeichert werden — heute genau eine: die
   * Wetter-Prognose, laut Konzept § 4.1 „ohnehin nicht persistiert" — gibt es keinen solchen
   * Zeitstempel. Der einzige wahre Stand ist dann der Zeitpunkt dieses Abrufs, und den kennt nur
   * der Client. Additiv wie `zusatz` (R0.11): Bestandsaufrufer ignorieren das Feld.
   */
  geladenAm: string | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [zusatz, setZusatz] = useState<Z | null>(null);
  const [geladenAm, setGeladenAm] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(path != null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // Welcher Pfad zuletzt geladen wurde und ob dabei etwas ankam - beides in Refs, damit der
  // Effekt nicht von seinem eigenen Ergebnis abhaengt (das wuerde ihn erneut ausloesen).
  const geladenerPfadRef = useRef<string | null>(null);
  const hatDatenRef = useRef(false);

  useEffect(() => {
    if (path == null) return;
    // H6: AbortController pro Effect-Aufruf statt eines ueber Renderzyklen geteilten Refs -
    // sonst kann eine veraltete Antwort (path A) den bereits geladenen State von path B
    // ueberschreiben, wenn A langsamer antwortet, aber der geteilte "alive"-Ref zwischenzeitlich
    // durch den B-Effect wieder auf true gesetzt wurde.
    const controller = new AbortController();
    // Nachladen desselben Pfades laesst den vorhandenen Inhalt stehen (siehe hooks/ladezustand.ts):
    // sonst blinkt jede Liste, ueber der ein Poll laeuft, im Takt des Polls.
    const still = !zeigtLadezustand(geladenerPfadRef.current, path, hatDatenRef.current);
    if (!still) setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await apiFetch(path, { signal: controller.signal });
        if (!res.ok) throw await toApiError(res);
        const body = (await res.json()) as ApiEnvelope<T, Z>;
        // `data` wird herausgeloest, der Rest bleibt als `zusatz` stehen — so kann `zusatz` nie
        // versehentlich eine zweite Kopie der Nutzlast fuehren.
        const { data: nutzlast, ...rest } = body;
        setData(nutzlast);
        setZusatz(rest as unknown as Z);
        setGeladenAm(new Date().toISOString());
        geladenerPfadRef.current = path;
        hatDatenRef.current = true;
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [path, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, zusatz, geladenAm, loading, error, reload };
}

/** Mutations-/Lese-Helfer (GET/POST/PUT/PATCH/DELETE): wirft bei nicht-OK, liefert das data-Feld. */
export async function apiMutate<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await apiFetch(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const json = (await res.json()) as { data: T };
  return json.data;
}

/**
 * Wie `apiMutate`, behaelt aber den Umschlag NEBEN `data` (z.B. `retroMatched` von
 * `POST /api/events`, `events.ts:611-618`) - additiv wie `ApiEnvelope`/`useApi`s `zusatz` (R0.11)
 * oben, damit `apiMutate` fuer seine ~60 Bestandsaufrufer unveraendert bleibt und nur Stellen, die
 * das Zusatzfeld wirklich brauchen (Migration 387, Bug 5: die Retro-Match-Race in
 * `neuenVorgangAnlegen`), den vollen Umschlag anfordern.
 */
export async function apiMutateMitZusatz<T, Z extends object = Record<string, never>>(
  path: string,
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  body?: unknown,
  signal?: AbortSignal,
): Promise<{ data: T } & Z> {
  const res = await apiFetch(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return { data: undefined as T } as { data: T } & Z;
  return (await res.json()) as { data: T } & Z;
}
